import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { map } from 'lodash'
import { sentenceCase } from 'change-case'
import { Box } from '@trybeapp/ui.box'
import { Button } from '@trybeapp/ui.button'
import { Card } from '../../../components/Card'
import { Col, Row } from '@trybeapp/ui.grid'
import { Tag } from '@trybeapp/ui.tag'
import { Text } from '@trybeapp/ui.text'
import { actions as orderActions } from '@trybeapp/ducks.orders'
import { STATUS_PENDING_CONFIRMATION } from 'constants/orderStatuses'
import { FontAwesomeIcon } from 'components/FontAwesomeIcon'
import { sentenceJoin } from 'utilities/StringUtils'
import { CostColumn, DateColumn, StartTimeColumn, StatusColumn, TypeColumn } from '../Columns'
import { useDeleteOrderItem } from 'api/OrderItems'
import { HeadingsRow } from './HeadingsRow'
import { useCanEditItem } from '../hooks'
import { ItemActions } from './ItemActions'
import { DropdownItem } from 'ui/components/Dropdown'
import { PencilIcon } from '@heroicons/react/outline'
import { useOrderDetails } from 'screens/OrderDetails/contexts'
import { ApiErrorNotification, useNotificationContext } from 'ui/components/Notification'

export const AreaBookingItemRow = ({ item }) => {
  const { orderId } = useParams()
  const { id: itemId, status: itemStatus, type_name: name = '', type_product_code: code } = item
  const canConfirm = itemStatus === STATUS_PENDING_CONFIRMATION
  const canEdit = useCanEditItem(item)
  const notification = useNotificationContext()

  const { mutate, isLoading: isDeleting } = useDeleteOrderItem()

  const handleDelete = () => {
    mutate([orderId, itemId], {
      onError: (error) => {
        notification.notify(<ApiErrorNotification error={error} />)
      },
    })
  }

  return (
    <Card className="my-2">
      <Card.Body>
        <Row>
          <Col col={0.7} mb="lg">
            <Box display="flex" alignItems="center">
              {code && (
                <Text as="span" variant="body3" color="nude.700" mr="xs">
                  <strong>[{code}]</strong>
                </Text>
              )}
              <Text as="span" variant="body3" color="nude.700">
                <strong>{name}</strong>
              </Text>
            </Box>
          </Col>
        </Row>
        <HeadingsRow />
        <Row>
          <StatusColumn item={item} />
          <DateColumn item={item} />
          <StartTimeColumn item={item} />
          <TypeColumn item={item}>
            <ResourceSummary item={item} />
            <ItemErrors item={item} />
          </TypeColumn>
          <CostColumn item={item} />
          <Col col={0.1}>
            <ItemActions item={item} handleDelete={handleDelete} isDeleting={isDeleting}>
              {canEdit && <EditItemButton item={item} />}
            </ItemActions>
            {canConfirm && (
              <Box>
                <ConfirmBasketItemButton item={item} />
              </Box>
            )}
          </Col>
        </Row>
      </Card.Body>
    </Card>
  )
}

const ResourceSummary = ({ item }) => {
  const { booking_summary: bookingSummary = {} } = item

  const resourceString = useMemo(() => {
    const areas = bookingSummary.areas || []

    const strings = []

    if (areas.length > 0) {
      strings.push(sentenceJoin(map(areas, 'name'), ',', '&'))
    }

    return sentenceCase(strings.join(' ') ?? '')
  }, [bookingSummary.areas])

  if (!resourceString) return null

  return (
    <Text variant="meta1" color="nude.500" lineHeight={1.2}>
      {resourceString}
    </Text>
  )
}

const useItemErrors = (item) => {
  const { booking_summary: bookingSummary = {} } = item
  const areas = bookingSummary.area_ids
  const [errors, setErrors] = useState([])

  useEffect(() => {
    const errorsSet = new Set(errors)

    if (!errorsSet.has('Area') && (areas === null || areas.length === 0)) {
      errorsSet.add('Area')
    }

    if (areas !== null && areas.length > 0 && errorsSet.has('Area')) {
      errorsSet.delete('Area')
    }

    setErrors(Array.from(errorsSet))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areas])

  return errors
}

const ItemErrors = ({ item }) => {
  const errors = useItemErrors(item)

  if (errors.length === 0) return null

  return (
    <Box mt="0.2rem">
      <Tag variant="danger">
        <FontAwesomeIcon icon="exclamation-circle" />
        {errors.join(', ')}
      </Tag>
    </Box>
  )
}

const EditItemButton = ({ item }) => {
  const { setValue } = useOrderDetails()

  const handleOnClick = () => {
    setValue('areaBookingWizard.editingAreaBooking', item)
  }

  return <DropdownItem label="Edit" onClick={handleOnClick} icon={PencilIcon} />
}

const ConfirmBasketItemButton = ({ item }) => {
  const dispatch = useDispatch()
  const { id: itemId } = item
  const { orderId } = useParams()

  const handleOnClick = () => dispatch(orderActions.confirmBookingOrderItem(orderId, itemId))

  return (
    <Button variant="secondary-success" mr="sm" size="sm" onClick={handleOnClick}>
      <FontAwesomeIcon icon="check-circle" mr="sm" />
      Confirm
    </Button>
  )
}
